import { FontSizePlugin } from '@platejs/basic-styles/react';
import { styled } from '@porsche-code/kado-styled-system/jsx';
import { PInputNumber } from '@porsche-design-system/components-react';
import { KEYS } from 'platejs';
import { useEditorState } from 'platejs/react';
import { type MouseEvent } from 'react';

import { getCurrentMarkValue, setLeafMark } from './shared';

const BASE_FONT_SIZE_IN_PX = 16;
const DEFAULT_FONT_SIZE_IN_PX = '16';
const MIN_FONT_SIZE_IN_PX = 8;
const MAX_FONT_SIZE_IN_PX = 48;

export const fontSizePlugin = FontSizePlugin.configure({
  inject: { targetPlugins: [KEYS.p] },
});

const FONT_SIZE_MARK = fontSizePlugin.key;

const FontSizeSelectContainer = styled('div', {
  base: {},
});

const parseFontSizeToPx = (value: string) => {
  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return '';
  }

  if (trimmedValue.endsWith('rem')) {
    const parsedValue = Number.parseFloat(trimmedValue);

    return Number.isFinite(parsedValue)
      ? String(Math.round(parsedValue * BASE_FONT_SIZE_IN_PX))
      : '';
  }

  if (trimmedValue.endsWith('px')) {
    const parsedValue = Number.parseFloat(trimmedValue);

    return Number.isFinite(parsedValue) ? String(Math.round(parsedValue)) : '';
  }

  const parsedValue = Number.parseFloat(trimmedValue);

  return Number.isFinite(parsedValue) ? String(Math.round(parsedValue)) : '';
};

const formatFontSizeMarkValue = (value: string) => {
  const parsedValue = Number.parseInt(value, 10);

  if (!Number.isFinite(parsedValue)) {
    return undefined;
  }

  return `${parsedValue}px`;
};

export const FontSizeToolbarControl = ({
  label = 'Font size',
}: {
  label?: string;
}) => {
  const editor = useEditorState();
  const currentFontSizeValue = parseFontSizeToPx(
    getCurrentMarkValue(editor, FONT_SIZE_MARK),
  );

  return (
    <FontSizeSelectContainer>
      <PInputNumber
        compact
        controls
        hideLabel
        name="fontSize"
        label={label}
        min={MIN_FONT_SIZE_IN_PX}
        max={MAX_FONT_SIZE_IN_PX}
        step={1}
        value={currentFontSizeValue || DEFAULT_FONT_SIZE_IN_PX}
        onClick={(event: MouseEvent<HTMLElement>) => event.stopPropagation()}
        onInput={(event: CustomEvent<InputEvent>) => {
          const nextValue = (
            event.target as HTMLInputElement & { value?: string }
          ).value;

          setLeafMark(
            editor,
            FONT_SIZE_MARK,
            formatFontSizeMarkValue(nextValue ?? ''),
          );
        }}
        onBlur={() => {
          const nextValue = parseFontSizeToPx(
            getCurrentMarkValue(editor, FONT_SIZE_MARK),
          );

          if (!nextValue) {
            setLeafMark(editor, FONT_SIZE_MARK, undefined);
          }
        }}
      />
    </FontSizeSelectContainer>
  );
};
